/**
 * =============================================================================
 * |   **CRITICAL-SYSTEM-FILE: DO NOT EDIT WITHOUT REVIEWING THE SYSTEM MANUAL**   |
 * =============================================================================
 * 
 * This file contains the core logic for the Chat page, which is one of the most
 * complex and sensitive parts of the application. It integrates with multiple
 * systems including real-time chat services, local database storage, and the
 * dynamic system prompt functionality.
 * 
 * Before making any changes, you **MUST** read and understand the following
 * section in the System Manual to avoid breaking critical functionality:
 * 
 * >> Section 3: Core Sub-System: The Chat Page
 * >> [../../docs/SYSTEM_MANUAL.md]
 * 
 * Failure to do so can result in application instability, data loss, or a
 * degraded user experience.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import { useSettings } from '../context/shared/SettingsContext';
import type { Message, ContentMode } from '../shared/types/src';
import ChatHeader from '../components/common/ChatHeader';
import ChatInput from '../components/common/ChatInput';
import ChatMessage from '../components/common/ChatMessage';
import apiService from '../services/shared/apiService';
import { AnimatePresence } from 'framer-motion';
import { ChatReviewModal } from '../components/ChatReviewModal';
import SaveMemoryModal from '../components/SaveMemoryModal';
import { v4 as uuidv4 } from 'uuid';

const Chat: React.FC = () => {
    const { user } = useAuth();
    const { themeClasses } = useTheme();
    const { settings } = useSettings();
    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isHistoryLoading, setIsHistoryLoading] = useState(true);
    const [isReviewModalOpen, setReviewModalOpen] = useState(false);
    const [isSaveMemoryModalOpen, setSaveMemoryModalOpen] = useState(false);
    const [memoryToSave, setMemoryToSave] = useState<any>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    
    // Prevent duplicate API calls in React StrictMode development
    const [hasLoadedHistory, setHasLoadedHistory] = useState(false);

    // Add state for model
    const [selectedModel, setSelectedModel] = useState<'Claude' | 'GPT'>('Claude');

    // Use contentMode from settings context instead of local state
    const contentMode = settings.contentMode;

    // Safe deduplication function to prevent repeated messages
    const deduplicateMessages = (messages: Message[]): Message[] => {
        const seen = new Set<string>();
        return messages.filter(msg => {
            // Create a unique key based on content, sender, and timestamp
            const key = `${msg.text}-${msg.sender}-${msg.timestamp}`;
            if (seen.has(key)) {
                console.warn('Duplicate message detected and filtered:', key);
                return false;
            }
            seen.add(key);
            return true;
        });
    };

    useEffect(() => {
        const fetchHistory = async () => {
            if (!user || hasLoadedHistory) return;
            console.log('Chat: Loading chat history for user:', user.uid);
            setHasLoadedHistory(true);
            setIsHistoryLoading(true);
            try {
                const historyData = await apiService.getChatHistory(user.uid);
                const deduplicatedHistory = deduplicateMessages(historyData.history || []);
                setMessages(deduplicatedHistory);
                console.log('Chat: Loaded', deduplicatedHistory.length, 'messages');
            } catch (error) {
                console.error("Failed to fetch chat history:", error);
            } finally {
                setIsHistoryLoading(false);
            }
        };
        fetchHistory();
    }, [user, hasLoadedHistory]);

    useEffect(() => {
        chatContainerRef.current?.scrollTo(0, chatContainerRef.current.scrollHeight);
    }, [messages]);

    const handleSendMessage = async (inputText: string) => {
        if (!inputText.trim() || !user) return;

        const userMessage: Message = {
            id: uuidv4(),
            text: inputText,
            sender: 'user',
            timestamp: new Date().toISOString(),
        };

        const thinkingMessage: Message = {
            id: uuidv4(),
            text: '...',
            sender: 'ai',
            timestamp: new Date().toISOString(),
        };

        setMessages(prev => [...prev, userMessage, thinkingMessage]);
        setIsLoading(true);

        try {
            const response = await apiService.sendChatMessage({
                message: inputText,
                model: selectedModel.toLowerCase(), // 'claude' or 'gpt'
                userId: user.uid,
                contentMode: contentMode,
                mindstreamMode: false,
                localHistory: messages
            });

            // Enhanced debug logging
            console.log('--- Chat API Raw Response ---');
            console.log(response);
            if (response && typeof response === 'object') {
                Object.entries(response).forEach(([k, v]) => {
                    console.log(`response[${k}]:`, v);
                });
            }
            console.log('modelUsed:', response.modelUsed); // Debug log
            console.log('llm_source:', response.llm_source); // Debug log

            // Defensive: fallback to modelUsed if llm_source is missing
            const llmSource = response.llm_source || response.modelUsed || 'unknown';
            console.log('Final llmSource for AI message:', llmSource);

            const aiMessage: Message = {
                id: `ai-${uuidv4()}`,
                text: response.response,
                sender: 'ai',
                llm_source: llmSource,
                timestamp: new Date().toISOString()
            };
            console.log('Created AI Message:', aiMessage); // Debug log
            
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== thinkingMessage.id);
                const withNewMessage = [...filtered, aiMessage];
                return deduplicateMessages(withNewMessage);
            });

        } catch (error: any) {
            console.error('Error sending message:', error);
            const errorMessage: Message = {
                id: thinkingMessage.id,
                text: "Sorry, I encountered an error. Please try again.",
                sender: 'ai',
                timestamp: new Date().toISOString(),
                isError: true,
            };
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== thinkingMessage.id);
                const withNewMessage = [...filtered, errorMessage];
                return deduplicateMessages(withNewMessage);
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveMemory = (aiMessage: Message) => {
        const aiIndex = messages.findIndex(msg => msg.id === aiMessage.id);
        const userPrompt = messages.slice(0, aiIndex).reverse().find(m => m.sender === 'user');
        setMemoryToSave({ aiMessage, userPrompt });
        setSaveMemoryModalOpen(true);
    };

    return (
        <div className={`flex flex-col h-[calc(100vh-120px)] bg-gray-100`}>
            <ChatHeader 
                onNewChat={() => setMessages([])} 
                selectedModel={selectedModel}
                onModelChange={setSelectedModel}
            />
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 min-h-0">
                <AnimatePresence initial={false}>
                    {messages.map((msg, index) => (
                        <ChatMessage 
                            key={msg.id || `${msg.timestamp}-${index}`} 
                            message={msg} 
                            onSave={handleSaveMemory} 
                        />
                    ))}
                </AnimatePresence>
            </div>
            <ChatInput onSendMessage={handleSendMessage} isSending={isLoading} />
            <ChatReviewModal 
                isOpen={isReviewModalOpen} 
                onClose={() => setReviewModalOpen(false)} 
                userId={user?.uid || ''}
                contentMode={contentMode === 'FULL' ? 'PG' : contentMode}
            />
            {isSaveMemoryModalOpen && memoryToSave && (
                <SaveMemoryModal 
                    isOpen={isSaveMemoryModalOpen} 
                    onClose={() => setSaveMemoryModalOpen(false)} 
                    messages={memoryToSave}
                    onSave={() => {
                        // Could trigger a refetch or show toast
                        setSaveMemoryModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default Chat;